import { ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { EllipsisVerticalIcon, IconButton, WidgetSizeEnum } from '@wonderschool/common-base-ui';

export type InvoiceAction = {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
};

type Coords = { top: number; left: number };

const MENU_WIDTH = 176; // Tailwind w-44
const GAP = 4;
const EDGE = 8;

/**
 * Row-action kebab for the invoices list.
 *
 * Why this exists instead of the shared `Dropdown`: that component is built on
 * Headless UI, which focuses the menu panel when it opens. On mobile the kebab
 * trigger is absolutely positioned in the card corner, so that focus made the
 * browser scroll the trigger into view and jump the whole page to the top,
 * leaving the menu off-screen. This menu never moves focus and renders through
 * a portal with `position: fixed` (viewport-relative), so opening it can't
 * scroll the page or get clipped by an ancestor's `overflow: hidden`.
 */
export function InvoiceActionsMenu({ actions, testId }: { actions: InvoiceAction[]; testId?: string }) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const computeCoords = useCallback((): Coords | null => {
    const anchor = anchorRef.current?.getBoundingClientRect();
    if (!anchor) return null;
    const menuHeight = menuRef.current?.offsetHeight ?? actions.length * 40 + 8;
    const left = Math.min(Math.max(anchor.right - MENU_WIDTH, EDGE), window.innerWidth - MENU_WIDTH - EDGE);
    // Flip above the trigger if it would overflow the bottom of the viewport.
    const belowTop = anchor.bottom + GAP;
    const top =
      belowTop + menuHeight > window.innerHeight - EDGE ? Math.max(anchor.top - menuHeight - GAP, EDGE) : belowTop;
    return { top, left };
  }, [actions.length]);

  // Position on open, then refine once the menu has a measured height, and keep
  // it aligned on resize. Any scroll closes the menu (standard dropdown behavior).
  useLayoutEffect(() => {
    if (!open) return;
    setCoords(computeCoords());
  }, [open, computeCoords]);

  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (event: Event) => {
      const target = event.target as Node;
      if (!menuRef.current?.contains(target) && !anchorRef.current?.contains(target)) setOpen(false);
    };
    // capture:true so we see the scroll even from nested containers.
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  return (
    <span ref={anchorRef} className="inline-flex">
      <IconButton
        data-testid={testId}
        Icon={EllipsisVerticalIcon}
        size={WidgetSizeEnum.X_SMALL}
        onClick={() => setOpen((prev) => !prev)}
      />
      {open &&
        coords &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ position: 'fixed', top: coords.top, left: coords.left, width: MENU_WIDTH, zIndex: 50 }}
            className="overflow-hidden rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none dark:bg-gray-800"
          >
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                role="menuitem"
                className="flex w-full items-center gap-x-2 px-3 py-2 text-left text-sm text-gray-900 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-700"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpen(false);
                  action.onClick();
                }}
              >
                {action.icon}
                {action.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </span>
  );
}
